<template>
  <!-- Drawer móvil -->
  <v-navigation-drawer
    v-model="appStore.drawer"
    color="primary"
    :disable-resize-watcher="true"
    class="d-lg-none"
    role="navigation"
    :aria-label="t('general.menuNavMovil')"
  >
    <v-list
      class="text-white drawer-list"
      :nav="true"
    >
      <template
        v-for="menu in menus"
        :key="menu.title"
      >
        <!-- Menú con submenús: acordeón -->
        <v-list-group
          v-if="menu.submenus?.length"
          :value="menu.title"
        >
          <template #activator="{ props: groupProps }">
            <v-list-item
              v-bind="groupProps"
              :prepend-icon="menu.icon"
              :active="isMenuActive(menu)"
              :class="{ 'drawer-active': isMenuActive(menu) }"
            >
              <v-list-item-title>{{ t(menu.title) }}</v-list-item-title>
            </v-list-item>
          </template>
          <v-list-item
            v-for="submenu in menu.submenus"
            :key="submenu.title"
            :prepend-icon="submenu.icon"
            :active="isRouteActive(submenu.url)"
            class="drawer-submenu-item"
            @click="handleSubmenuClick(submenu); appStore.drawer = false"
          >
            <v-list-item-title>{{ t(submenu.title) }}</v-list-item-title>
            <template
              v-if="submenu.ext"
              #append
            >
              <v-icon size="x-small">
                mdi-open-in-new
              </v-icon>
            </template>
          </v-list-item>
        </v-list-group>

        <!-- Menú simple -->
        <v-list-item
          v-else
          :prepend-icon="menu.icon"
          :active="isMenuActive(menu)"
          :class="{ 'drawer-active': isMenuActive(menu) }"
          :aria-current="isMenuActive(menu) ? 'page' : undefined"
          @click="handleMenuClick(menu)"
        >
          <v-list-item-title>{{ t(menu.title) }}</v-list-item-title>
        </v-list-item>
      </template>
    </v-list>
  </v-navigation-drawer>

  <!-- BARRA COMBINADA: Menú + Breadcrumb en desktop -->
  <v-app-bar
    color="primary"
    :elevation="2"
    density="compact"
    class="d-none d-lg-flex combined-bar"
    role="navigation"
  >
    <v-container
      fluid
      class="responsive-container menu-container"
    >
      <div class="d-flex align-center justify-space-between">
        <!-- IZQUIERDA: Menú -->
        <div class="d-flex align-center menu-section">
          <template
            v-for="menu in menus"
            :key="menu.title"
          >
            <!-- Menú sin submenús -->
            <v-btn
              v-if="!menu.submenus?.length"
              color="primary"
              variant="flat"
              size="small"
              class="menu-btn"
              :class="{ 'active-btn': isMenuActive(menu) }"
              :prepend-icon="menu.icon"
              @mouseenter="prefetchRoute(menu.url)"
              @focusin="prefetchRoute(menu.url)"
              @click="handleMenuClick(menu)"
            >
              {{ t(menu.title) }}
            </v-btn>

            <!-- Menú con submenús (dropdown manual) -->
            <div
              v-else
              class="submenu-wrapper"
            >
              <v-btn
                color="primary"
                variant="flat"
                size="small"
                class="menu-btn"
                :class="{ 'active-btn': isMenuActive(menu) }"
                :prepend-icon="menu.icon"
                :aria-expanded="openSubmenu === menu.title"
                aria-haspopup="true"
                @click.stop="openSubmenu = openSubmenu === menu.title ? null : menu.title"
              >
                {{ t(menu.title) }}
                <v-icon
                  size="x-small"
                  class="ml-1"
                >
                  mdi-chevron-down
                </v-icon>
              </v-btn>

              <div
                v-if="openSubmenu === menu.title"
                v-click-outside="closeSubmenu"
                class="submenu-dropdown"
                role="menu"
              >
                <div class="submenu-arrow" />
                <div
                  v-for="submenu in menu.submenus"
                  :key="submenu.title"
                  class="submenu-item"
                  :class="{ 'submenu-item--active': isRouteActive(submenu.url) }"
                  role="menuitem"
                  tabindex="0"
                  @click="handleSubmenuClick(submenu); openSubmenu = null"
                  @keydown.enter="handleSubmenuClick(submenu); openSubmenu = null"
                  @keydown.space.prevent="handleSubmenuClick(submenu); openSubmenu = null"
                  @keydown.escape="openSubmenu = null"
                >
                  <div
                    class="submenu-icon-wrapper"
                    :style="isRouteActive(submenu.url)
                      ? { background: submenu.color || 'rgb(var(--v-theme-primary))', color: '#fff' }
                      : { background: (submenu.color || '#2f4858') + '12', color: submenu.color || '#2f4858' }"
                  >
                    <v-icon size="20">
                      {{ submenu.icon }}
                    </v-icon>
                  </div>
                  <div class="submenu-content">
                    <span class="submenu-text">
                      {{ t(submenu.title) }}
                      <v-icon
                        v-if="submenu.ext"
                        size="11"
                        class="submenu-external"
                      >
                        mdi-open-in-new
                      </v-icon>
                    </span>
                    <span
                      v-if="submenu.desc"
                      class="submenu-desc"
                    >{{ t(submenu.desc) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>

        <!-- DERECHA: Breadcrumb integrado con logo CNMC -->
        <nav
          class="breadcrumb-inline"
          :aria-label="t('general.ubicacionActual')"
        >
          <button
            class="breadcrumb-link"
            @click="
              openExternalLink(
                'https://www.cnmc.es/sectores-que-regulamos/telecomunicaciones/calidad-servicios'
              )
            "
          >
            <img
              :src="logoCuadrado"
              alt=""
              role="presentation"
              class="cnmc-icon"
              width="16"
              height="16"
            >
            {{ t("menu.calidadServicioTeleco") }}
            <v-icon
              size="10"
              class="ml-1"
            >
              mdi-open-in-new
            </v-icon>
          </button>

          <span class="breadcrumb-separator">›</span>

          <button
            class="breadcrumb-link"
            :class="{ 'breadcrumb-active': isRouteActive('home') }"
            :disabled="isRouteActive('home')"
            @click="navigateTo('home')"
          >
            {{ t("migasPan.principal") }}
          </button>

          <template v-if="!isHome">
            <template
              v-for="(miga, index) in breadcrumbItems"
              :key="index"
            >
              <span class="breadcrumb-separator">›</span>
              <button
                class="breadcrumb-link"
                :class="{ 'breadcrumb-active': miga.disabled }"
                :disabled="miga.disabled"
                @click="!miga.disabled && navigateTo(miga.baseName)"
              >
                {{ miga.title }}
              </button>
            </template>
          </template>
        </nav>
      </div>
    </v-container>
  </v-app-bar>

  <!-- Breadcrumb móvil - VERSIÓN COMPACTA -->
  <div class="d-lg-none mobile-breadcrumb">
    <v-container class="responsive-container py-2">
      <nav
        class="breadcrumb-mobile"
        aria-label="Ubicación actual"
      >
        <button
          class="breadcrumb-link-mobile"
          @click="
            openExternalLink(
              'https://www.cnmc.es/sectores-que-regulamos/telecomunicaciones/calidad-servicios'
            )
          "
        >
          <img
            :src="logoCuadrado"
            alt=""
            role="presentation"
            class="cnmc-icon-mobile"
            width="14"
            height="14"
          >
          <span class="d-none d-sm-inline">{{
            t("menu.calidadServicioTeleco")
          }}</span>
          <span class="d-sm-none">CNMC</span>
          <v-icon
            size="10"
            class="ml-1"
          >
            mdi-open-in-new
          </v-icon>
        </button>
        <span class="breadcrumb-separator-mobile">›</span>
        <button
          class="breadcrumb-link-mobile"
          :class="{ 'breadcrumb-active-mobile': isRouteActive('home') }"
          :disabled="isRouteActive('home')"
          @click="navigateTo('home')"
        >
          {{ t("migasPan.principal") }}
        </button>
        <template v-if="!isHome">
          <template
            v-for="(miga, index) in breadcrumbItems"
            :key="index"
          >
            <span class="breadcrumb-separator-mobile">›</span>
            <button
              class="breadcrumb-link-mobile"
              :class="{ 'breadcrumb-active-mobile': miga.disabled }"
              :disabled="miga.disabled"
              @click="!miga.disabled && navigateTo(miga.baseName)"
            >
              {{ miga.title }}
            </button>
          </template>
        </template>
      </nav>
    </v-container>
  </div>

  <!-- Modal móvil -->
  <v-dialog
    v-model="showMobileDialog"
    max-width="400"
    persistent
  >
    <v-card class="mobile-dialog-simple rounded-xl">
      <v-card-text class="pa-6 text-center">
        <div class="mb-4 logo-wrapper-modal">
          <img
            :src="logoCNMC"
            alt="Logo CNMC"
            class="cnmc-logo-modal"
            width="240"
            height="26"
          >
        </div>
        <v-divider class="my-4" />
        <h2 class="text-h6 font-weight-bold mb-2">
          {{ t("home.modal.titulo_simple", "Mejor en la app") }}
        </h2>
        <p class="text-body-2 text-medium-emphasis mb-5">
          {{
            t(
              "home.modal.descripcion_simple",
              "Mediciones más precisas y fiables"
            )
          }}
        </p>
        <div class="modal-badges-container mb-4">
          <a
            v-if="!isIOS"
            href="https://play.google.com/store/apps/details?id=es.gob.cnmc"
            target="_blank"
            rel="noopener noreferrer"
            class="modal-store-badge"
          >
            <img
              :src="googlePlayBadgeUrl"
              alt="Google Play"
              class="modal-badge-img"
              width="168"
              height="50"
            >
          </a>
          <a
            v-if="isIOS"
            href="https://apps.apple.com/us/app/test-velocidad/id6476399770"
            target="_blank"
            rel="noopener noreferrer"
            class="modal-store-badge"
          >
            <img
              :src="appStoreBadgeUrl"
              alt="App Store"
              class="modal-badge-img"
              width="150"
              height="50"
            >
          </a>
        </div>
        <v-divider class="my-4" />
        <v-btn
          variant="text"
          size="default"
          class="text-none"
          block
          @click="continueInBrowser"
        >
          <v-icon
            start
            size="small"
          >
            mdi-web
          </v-icon>
          <span class="text-medium-emphasis">
            {{ t("home.modal.continuar_web", "Usar versión web") }}
          </span>
        </v-btn>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { useAppStore } from "@/stores/app.store";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";
import { ref, computed, onMounted, onUnmounted } from "vue";
import { getLocaleFromRoute, getBaseRouteName } from "@/router";
import { isProductionHost } from "@/utils/env";

import googlePlayES from "@/assets/badges/google-play-es.png";
import googlePlayEN from "@/assets/badges/google-play-en.png";
import appStoreES from "@/assets/badges/app-store-es.svg";
import appStoreEN from "@/assets/badges/app-store-en.svg";
import logoCNMC from "@/assets/CNMC_Logotipo.png";
import logoCuadrado from "@/assets/logo.jpg";

const appStore = useAppStore();
const { t, locale } = useI18n({ useScope: "global" });
const $router = useRouter();
const route = useRoute();

const showMobileDialog = ref(false);
// `false` durante SSR (sin window). Se actualiza en onMounted con el hostname real.
// Permite filtrar entradas del menú marcadas como `hiddenInPro` en el array `allMenus`.
const isProd = ref(false);
const isMobile = ref(typeof window !== 'undefined' ? window.innerWidth < 960 : false);
const isIOS = ref(typeof navigator !== 'undefined' ? /iPad|iPhone|iPod/.test(navigator.userAgent) : false);
const openSubmenu = ref(null);

const closeSubmenu = () => {
  openSubmenu.value = null;
};

// Obtener el locale actual desde la URL
const currentLocale = computed(() => getLocaleFromRoute(route));

// Obtener el nombre de ruta base (sin sufijo -en)
const currentBaseName = computed(() => getBaseRouteName(route.name));

const checkIfMobile = () => {
  isMobile.value = window.innerWidth < 960;
};

onMounted(() => {
  checkIfMobile();
  window.addEventListener("resize", checkIfMobile);
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  isIOS.value = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  isProd.value = isProductionHost();
});

onUnmounted(() => {
  window.removeEventListener("resize", checkIfMobile);
});

const isHome = computed(() => currentBaseName.value === "home");

// Verificar si una ruta está activa (comparando nombre base)
const isRouteActive = (baseName) => {
  return currentBaseName.value === baseName;
};

// Filtrar rutas que no son páginas reales (como contenedores de submenús)
const breadcrumbItems = computed(() => {
  const excludedRoutes = ["home", "masInformacion", "masInfo"];

  return route.matched
    .filter((match) => {
      const baseName = getBaseRouteName(match.name);
      return baseName && !excludedRoutes.includes(baseName);
    })
    .map((match) => {
      const baseName = getBaseRouteName(match.name);
      return {
        title: t(`migasPan.${baseName}`),
        baseName: baseName,
        disabled: currentBaseName.value === baseName,
      };
    });
});

const openExternalLink = (url) => {
  window.open(url, "_blank", "noopener,noreferrer");
};

// Prefetch de chunks al hacer hover en menú (precarga el JS antes del clic)
const chunkLoaders = {
  home: () => import("@/pages/Home"),
  test: () => import("@/pages/SpeedTest/TestDemo"),
  mapa: () => import("@/pages/ViewMap/MapV2"),
  datos: () => import("@/pages/OpenData/OpenTests"),
  historial: () => import("@/pages/History/History"),
  estadisticas: () => import("@/pages/Statistics/Statistics"),
};
const prefetchedRoutes = new Set();
const prefetchRoute = (url) => {
  if (!url || prefetchedRoutes.has(url) || !chunkLoaders[url]) return;
  prefetchedRoutes.add(url);
  chunkLoaders[url]();
};

const startTestFromHome = () => {
  if (isMobile.value) {
    showMobileDialog.value = true;
  } else {
    navigateTo("test", { autoStart: "true" });
  }
};

const continueInBrowser = () => {
  showMobileDialog.value = false;
  navigateTo("test", { autoStart: "true" });
};

// Navegar manteniendo el idioma actual
const navigateTo = (baseName, query = {}) => {
  if (baseName) {
    const routeName =
      currentLocale.value === "en" ? `${baseName}-en` : baseName;
    $router.push({ name: routeName, query });
  }
};

const handleMenuClick = (menu) => {
  if (!menu.submenus?.length) {
    if (menu.url === "test") {
      startTestFromHome();
    } else {
      navigateTo(menu.url);
    }
  }
};

const handleSubmenuClick = (submenu) => {
  if (submenu.ext) {
    window.open(submenu.ext, "_blank", "noopener,noreferrer");
  } else {
    navigateTo(submenu.url);
  }
};

const isMenuActive = (menu) => {
  if (menu.url) {
    return isRouteActive(menu.url);
  }
  if (menu.submenus?.length) {
    return menu.submenus.some((sub) => isRouteActive(sub.url));
  }
  return false;
};

const googlePlayBadgeUrl = computed(() => {
  return locale.value === "es" ? googlePlayES : googlePlayEN;
});

const appStoreBadgeUrl = computed(() => {
  return locale.value === "es" ? appStoreES : appStoreEN;
});

// Definicion cruda. Items con `hiddenInPro: true` se filtran reactivamente
// segun el hostname actual (ver computed `menus` mas abajo). Util para esconder
// secciones en produccion mientras esperan validacion sin tocar la ruta.
const allMenus = [
  { title: "menu.principal", url: "home", icon: "mdi-home" },
  { title: "menu.empezarTest", url: "test", icon: "mdi-speedometer" },
  { title: "menu.historial", url: "historial", icon: "mdi-history" },
  { title: "menu.verMapa", url: "mapa", icon: "mdi-map-marker-radius" },
  { title: "menu.estadisticas", url: "estadisticas", icon: "mdi-chart-bar" },
  { title: "menu.datosAbiertos", url: "datos", icon: "mdi-database-search" },
  {
    title: "menu.masInfo",
    url: "",
    icon: "mdi-information-outline",
    submenus: [
      {
        title: "menu.servicios",
        desc: "menu.servicios_desc",
        url: "servicios",
        icon: "mdi-briefcase-outline",
        color: "#2f4858",
      },
      {
        title: "menu.faq",
        desc: "menu.faq_desc",
        url: "faq",
        icon: "mdi-help-circle-outline",
        color: "#0891b2",
      },
      {
        title: "menu.appsMoviles",
        desc: "menu.appsMoviles_desc",
        url: "apps",
        icon: "mdi-cellphone",
        color: "#059669",
      },
      {
        title: "menu.iframe",
        desc: "menu.iframe_desc",
        url: "iframe",
        icon: "mdi-code-tags",
        color: "#7c3aed",
      },
      {
        title: "menu.medios",
        desc: "menu.medios_desc",
        url: "medios",
        icon: "mdi-newspaper-variant-outline",
        color: "#64748b",
      },
      {
        title: "menu.derechos",
        desc: "menu.derechos_desc",
        url: "derechos",
        icon: "mdi-gavel",
        color: "#b91c1c",
        // Solo visible fuera de produccion mientras la pagina espera validacion
        // juridica y coordinacion con el Ministerio (Dracma 74910). La ruta tambien
        // tiene isHidden en su meta — el router redirige a "/" en pro como defensa.
        hiddenInPro: true,
      },
      {
        title: "menu.opiniones",
        desc: "menu.opiniones_desc",
        url: "opiniones",
        icon: "mdi-star",
        color: "#f59e0b",
      },
      {
        title: "menu.configuracion",
        desc: "menu.configuracion_desc",
        url: "configuracion",
        icon: "mdi-cog-outline",
        color: "#64748b",
      },
    ],
  },
];

// Vista filtrada del menu: en produccion oculta items (y subitems) marcados con
// `hiddenInPro`. En pre/local se ven todos. La ref `isProd` se inicializa en
// `onMounted` con el hostname real, asi que durante SSG no se filtra nada.
const menus = computed(() => {
  if (!isProd.value) return allMenus;
  return allMenus
    .filter((m) => !m.hiddenInPro)
    .map((m) => {
      if (!m.submenus?.length) return m;
      return { ...m, submenus: m.submenus.filter((s) => !s.hiddenInPro) };
    });
});
</script>

<style scoped>
.v-btn {
  text-transform: none;
}

/* BARRA COMBINADA */
.combined-bar {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.combined-bar :deep(.v-toolbar__content) {
  overflow: visible !important;
  height: auto !important;
  min-height: 48px;
  padding: 4px 0;
}

.menu-container {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

/* Responsive container - DEBE coincidir con App.vue */
.responsive-container {
  max-width: 1400px;
  margin: 0 auto;
  padding-left: 16px;
  padding-right: 16px;
}

@media (max-width: 768px) {
  .responsive-container {
    max-width: 100%;
    padding-left: 10px;
    padding-right: 10px;
  }
}

@media (min-width: 1280px) {
  .responsive-container {
    max-width: 1340px;
  }
}

@media (min-width: 1600px) {
  .responsive-container {
    max-width: 1540px;
  }
}

@media (min-width: 1920px) {
  .responsive-container {
    max-width: 1840px;
  }
}

@media (min-width: 2560px) {
  .responsive-container {
    max-width: 2400px;
  }
}

.menu-section {
  gap: 6px;
}

/* Botones del menú - ÁREA CLICKEABLE GRANDE */
.menu-btn {
  font-weight: 500;
  font-size: 0.875rem;
  letter-spacing: 0.2px;
  transition: all 0.2s ease;
  padding: 0 16px !important;
  height: 40px !important;
  min-width: auto !important;
  border-radius: 8px !important;
  white-space: nowrap;
}

.menu-btn:hover {
  background-color: rgba(255, 255, 255, 0.12) !important;
}

.menu-btn:focus-visible {
  outline: 2px solid rgba(255, 255, 255, 0.6);
  outline-offset: 2px;
}

.active-btn {
  background-color: rgba(255, 255, 255, 0.18) !important;
  font-weight: 600;
}

/* BREADCRUMB INLINE (desktop) - SIN FONDO */
.breadcrumb-inline {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
}

.breadcrumb-link {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 2px 0;
  display: inline-flex;
  align-items: center;
}

.breadcrumb-link:hover:not(:disabled) {
  color: white;
  text-decoration: underline;
}

.breadcrumb-link:disabled {
  cursor: default;
}

.breadcrumb-active {
  color: white !important;
  font-weight: 700;
}

.breadcrumb-separator {
  color: rgba(255, 255, 255, 0.6);
  font-weight: 400;
  font-size: 1rem;
}

.breadcrumb-link:focus-visible {
  outline: 2px solid rgba(255, 255, 255, 0.8);
  outline-offset: 2px;
  border-radius: 2px;
}

/* Icono CNMC - IMAGEN */
.cnmc-icon {
  width: 16px;
  height: 16px;
  margin-right: 6px;
  flex-shrink: 0;
  border-radius: 2px;
  object-fit: cover;
}

/* BREADCRUMB MÓVIL - COMPACTO */
/* BREADCRUMB MÓVIL - SIN FONDO, LIMPIO */
.mobile-breadcrumb {
  background: transparent;
  border-bottom: none;
  margin-top: -8px;
  padding: 0.5rem 0.5rem 0;
}

.breadcrumb-mobile {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8125rem;
  overflow-x: auto;
  white-space: nowrap;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.breadcrumb-mobile::-webkit-scrollbar {
  display: none;
}

.breadcrumb-link-mobile {
  background: none;
  border: none;
  color: rgb(var(--v-theme-primary));
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 4px 0;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.breadcrumb-link-mobile:hover:not(:disabled) {
  color: #1a2f3a;
  text-decoration: underline;
}

.breadcrumb-link-mobile:disabled {
  cursor: default;
}

.breadcrumb-active-mobile {
  color: rgb(var(--v-theme-primary)) !important;
  font-weight: 700;
}

.breadcrumb-separator-mobile {
  color: rgba(0, 0, 0, 0.4);
  font-weight: 400;
  flex-shrink: 0;
}

.breadcrumb-link-mobile:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
  border-radius: 2px;
}

.cnmc-icon-mobile {
  width: 14px;
  height: 14px;
  margin-right: 4px;
  flex-shrink: 0;
  border-radius: 2px;
  object-fit: cover;
}

/* SUBMENÚS */
.submenu-wrapper {
  position: relative;
}

.submenu-dropdown {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 12px;
  z-index: 1000;
  border-radius: 14px;
  overflow: visible;
  min-width: 280px;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -2px rgba(0, 0, 0, 0.1),
    0 20px 40px -10px rgba(0, 0, 0, 0.2);
  animation: submenuFadeIn 0.15s ease;
}

@keyframes submenuFadeIn {
  from { opacity: 0; transform: translateX(-50%) translateY(-4px); }
  to { opacity: 1; transform: translateX(-50%) translateY(0); }
}

.submenu-arrow {
  position: absolute;
  top: -6px;
  left: 50%;
  transform: translateX(-50%);
  width: 12px;
  height: 12px;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-bottom: none;
  border-right: none;
  rotate: 45deg;
  z-index: 1;
}

/* Items del submenú */
.submenu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.15s ease;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  position: relative;
}

.submenu-item:first-of-type {
  border-radius: 14px 14px 0 0;
}

.submenu-item:last-child {
  border-bottom: none;
  border-radius: 0 0 14px 14px;
}

.submenu-item:hover {
  background: var(--color-background-subtle);
}

.submenu-item:focus {
  outline: none;
  background: var(--color-background-subtle);
}

.submenu-item:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

.submenu-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  flex-shrink: 0;
  transition: all 0.15s ease;
}

.submenu-item:hover .submenu-icon-wrapper {
  transform: scale(1.08);
}

.submenu-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.submenu-text {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  gap: 4px;
}

.submenu-desc {
  font-size: 0.75rem;
  color: var(--color-text-secondary);
  font-weight: 400;
  line-height: 1.3;
}

.submenu-item:hover .submenu-text {
  color: rgb(var(--v-theme-primary));
}

.submenu-external {
  color: #cbd5e1;
}

/* Item activo */
.submenu-item--active {
  background: #f1f5f9;
}

.submenu-item--active .submenu-text {
  color: rgb(var(--v-theme-primary));
}

/* DRAWER */
.drawer-active {
  background-color: rgba(255, 255, 255, 0.12) !important;
  border-left: 3px solid #ffffff;
  font-weight: 600;
}

/* Drawer: espacio entre icono y texto */
.drawer-list :deep(.v-list-item__prepend) {
  margin-inline-end: 0 !important;
  display: inline !important;
  width: 24px !important;
}

.drawer-list :deep(.v-list-item) {
  column-gap: 10px !important;
  padding-inline-start: 12px !important;
  min-height: 44px !important;
  padding-top: 4px !important;
  padding-bottom: 4px !important;
  border-radius: 8px;
  margin-bottom: 2px;
}

.drawer-list :deep(.v-list-item:hover) {
  background-color: rgba(255, 255, 255, 0.08) !important;
}

.drawer-list :deep(.v-list-item__prepend > .v-icon),
.drawer-list :deep(.v-list-item__append > .v-icon),
.drawer-list :deep(.v-list-group__header .v-icon) {
  color: white !important;
  opacity: 0.9 !important;
}

.drawer-list :deep(.v-list-item-title) {
  font-size: 0.9rem !important;
  font-weight: 500;
  letter-spacing: 0.01em;
}

/* Submenús dentro del drawer */
.drawer-list :deep(.v-list-group__items) {
  padding-inline-start: 16px !important;
  border-left: 2px solid rgba(255, 255, 255, 0.15);
  margin-left: 20px;
  margin-top: 2px;
  margin-bottom: 4px;
}

.drawer-list :deep(.v-list-group__items .v-list-item) {
  min-height: 40px !important;
  padding-top: 2px !important;
  padding-bottom: 2px !important;
  padding-inline-start: 10px !important;
}

.drawer-list :deep(.v-list-group__items .v-list-item .v-list-item__prepend) {
  margin-inline-end: 0 !important;
  display: inline !important;
}

.drawer-list :deep(.v-list-group__items .v-list-item .v-icon) {
  font-size: 18px !important;
  opacity: 0.7;
}

.drawer-list :deep(.v-list-group__items .v-list-item .v-list-item-title) {
  font-size: 0.84rem !important;
  opacity: 0.85;
}

/* MODAL */
.logo-wrapper-modal {
  display: flex;
  justify-content: center;
  padding: 12px;
}

.cnmc-logo-modal {
  max-width: 240px;
  width: 100%;
  height: auto;
}

.modal-badges-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
}

.modal-store-badge {
  display: inline-block;
  transition: transform 0.2s ease;
}

.modal-store-badge:hover {
  transform: scale(1.05);
}

.modal-badge-img {
  height: 50px;
  object-fit: contain;
}

/* RESPONSIVE */
@media (max-width: 1400px) {
  .menu-btn {
    padding: 0 10px !important;
    height: 38px !important;
    font-size: 0.8125rem;
  }

  .menu-section {
    gap: 4px;
  }

  .breadcrumb-inline {
    font-size: 0.8125rem;
  }
}

@media (max-width: 1280px) {
  .menu-btn {
    padding: 0 8px !important;
    height: 36px !important;
    font-size: 0.75rem;
  }

  .menu-btn :deep(.v-btn__prepend) {
    display: none;
  }

  .cnmc-icon {
    width: 14px;
    height: 14px;
  }
}

@media (max-width: 1100px) {
  .menu-btn {
    padding: 0 6px !important;
    height: 36px !important;
    font-size: 0.7rem;
  }

  .menu-section {
    gap: 2px;
  }
}

@media (max-width: 600px) {
  .breadcrumb-mobile {
    font-size: 0.75rem;
    gap: 4px;
  }

  .cnmc-icon-mobile {
    width: 16px;
    height: 16px;
  }

  .modal-badge-img {
    height: 44px;
  }
}
</style>
